import {
  Popover,
  Space,
  Spin,
  Table,
  Tag,
  Modal,
  Button,
  DatePicker,
} from "antd";
import { TableRowSelection } from "antd/es/table/interface";
import moment from "moment";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { getApi, putApi } from "redux/apis";
import { pageSizeOptionsPaging } from "utils/constant";
import { EditActionSvg, SortSvgBottom, SortSvgTop } from "utils/svgs";
import { invalidText, toastText } from "utils/utils";
import {
  SEARCHABLE_FIELDS,
  SHIPMENT_STATUS_CONFIG,
} from "../../../../utils/shipment";
import SearchAndFilter from "../SearchAndFilter";
import styles from "./index.module.scss";
import { ShipmentTableProps } from "./types";
import { UploadOutlined, ExportOutlined, EyeOutlined } from "@ant-design/icons";
import DropdownComponent from "components/Global/Dropdown";
import dayjs from "dayjs";

const { Column } = Table;

const SortingIcon = (data: any) =>
  data.data.sortOrder === "ascend" ? <SortSvgTop /> : <SortSvgBottom />;

const ShipmentTable = (props: any) => {
  const {
    allShipmentData,
    changeDateState,
    isLoading,
    totalRecords,
    searchValue,
    sortDirection,
    searchFields,
    performSearchHandler,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    fetchShipments,
    tableChangeHandler,
    isServiceCenter,
    setSelectedShipment,
    openModalHandler,
    selectedRowKeys,
    setSelectedRowKeys,
    handleServiceCenterChange,
    selectedServiceCenter,
    handleOk,
    handleSalesOrderNumberChange,
    setFiltersReset,
    selectedSalesOrderNumbers,
    salesOrderNumberOptions,
    fetchSalesOrderNumber,
    selectedStatus,
    handleStatusChange,
    setSelectedStatus,
    baseLoading,
    setSelectedDate,
    setSelectedRecordId,
    recordId,
    selectedDate,
    setRefetchAgain,
    handleFilterCall,
    clearFilter,
    handleDeleteClick,
    rowSelection,
  } = props;

  // States for managing modal and child data
  const [localUpdates, setLocalUpdates] = useState<Record<string, any>>({});
  const [serviceCenters, setServiceCenters] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalData, setModalData] = useState<any>(null);
  const [childData, setChildData] = useState<any[]>([]);
  const [childPage, setChildPage] = useState<number>(1);
  const [childTotal, setChildTotal] = useState<number>(0);
  const [bulkUpdateDate, setBulkUpdateDate] = useState<string | null>(null);
  const [isLoadingChildData, setIsLoadingChildData] = useState(false);
  const [childSortField, setChildSortField] = useState<string | null>(null);
  const [childSortOrder, setChildSortOrder] = useState<
    "ascend" | "descend" | null
  >(null);
  const [updateRecords, setUpdateRecords] = useState(null);

  // IMPROVED: Consolidated loading states for smoother experience
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);

  const [modalSelectedRowKeys, setModalSelectedRowKeys] = useState<any[]>([]);

  // Keep a separate clean copy of parent data
  const [parentData, setParentData] = useState<any[]>([]);

  // Use a ref to track if the component is mounted
  const isMounted = useRef(true);
  const processingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handlechildDataChange = (data: any[]) => {
    setChildData(data);
  };

  // useEffect(() => {
  //   return () => {
  //     isMounted.current = false;
  //     if (processingTimeoutRef.current) {
  //       clearTimeout(processingTimeoutRef.current);
  //     }
  //   };
  // }, []);

  // Add service centers fetch effect
  useEffect(() => {
    const fetchServiceCenters = async () => {
      try {
        const response = await getApi("/serviceCenter/shipment-dropdown");
        if (response.data.success) {
          const formattedCenters = response.data.data.map((center: any) => ({
            value: center.id,
            label: center.name,
          }));
          setServiceCenters(formattedCenters);
        }
      } catch (error) {
        console.error("Failed to fetch service centers:", error);
      }
    };

    fetchServiceCenters();
  }, []);

  // IMPROVED: Smooth data processing with debounced loading
  const processShipmentData = useCallback((data: any[]) => {
    return new Promise<any[]>((resolve) => {
      // Use requestAnimationFrame for smooth processing
      requestAnimationFrame(() => {
        try {
          const groupedData = data.reduce((acc, item) => {
            const groupKey = `${item.serviceCenterName}-${item.salesOrderNumber}`;

            if (!acc[groupKey]) {
              const { children, ...parentData } = item;
              acc[groupKey] = {
                ...parentData,
                shipmentQty: children
                  ? children.reduce(
                      (sum, child) => sum + (child.quantity || 0),
                      0
                    )
                  : 0,
              };
            }

            return acc;
          }, {});

          const cleanParentData = Object.values(groupedData);
          resolve(cleanParentData);
        } catch (error) {
          console.error("Error processing shipment data:", error);
          resolve([]);
        }
      });
    });
  }, []);

  // IMPROVED: Process allShipmentData with smooth loading
  useEffect(() => {
    if (allShipmentData && Array.isArray(allShipmentData)) {
      setIsTableLoading(true);

      // Clear any existing timeout
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }

      const processData = async () => {
        try {
          const cleanParentData = await processShipmentData(allShipmentData);

          if (isMounted.current) {
            setParentData(cleanParentData);

            // If modal is open, update modal data
            if (isModalVisible && modalData) {
              setIsModalLoading(true);

              const matchingParent = allShipmentData.find(
                (item) =>
                  item.serviceCenterId === modalData.serviceCenterId &&
                  item.salesOrderNumber === modalData.salesOrderNumber
              );

              if (matchingParent && matchingParent.children) {
                matchingParent.children = matchingParent.children.map(
                  (child) => ({
                    ...child,
                    salesOrderDate: matchingParent.salesOrderDate,
                  })
                );

                setChildData(matchingParent.children);
                setChildTotal(matchingParent.children.length);
                setModalData((prev) => ({
                  ...prev,
                  ...matchingParent,
                }));
              } else {
                // Close modal if no matching data found
                handleModalClose();
                return;
              }

              // Minimum loading time for modal refresh
              setTimeout(() => {
                if (isMounted.current) {
                  setIsModalLoading(false);
                }
              }, 200);
            }
          }
        } catch (error) {
          console.error("Error processing shipment data:", error);
        } finally {
          // Minimum loading time for smooth UX
          processingTimeoutRef.current = setTimeout(() => {
            if (isMounted.current) {
              setIsTableLoading(false);
            }
          }, 150);
        }
      };

      processData();
    }
  }, [
    allShipmentData,
    isModalVisible,
    modalData?.serviceCenterId,
    modalData?.salesOrderNumber,
    processShipmentData,
  ]);

  // Function to get unique key for each group
  const getGroupKey = useCallback((record: any) => {
    if (!record) {
      console.warn("getGroupKey called with null/undefined record");
      return `empty-${Math.random()}`;
    }

    if (!record.serviceCenterName || !record.salesOrderNumber) {
      console.warn("Missing required fields for rowKey:", record);
      return `incomplete-${record.id || Math.random()}`;
    }

    return `${record.serviceCenterName}-${record.salesOrderNumber}`;
  }, []);

  // Function to fetch children by group
  const fetchShipmentsByGroup = useCallback(
    async ({
      serviceCenterId,
      salesOrderNumber,
      page,
      sortField = null,
      sortOrder = null,
    }: {
      serviceCenterId: string;
      salesOrderNumber: string;
      page: number;
      sortField?: string | null;
      sortOrder?: string | null;
    }): Promise<{ data: any[]; total: number }> => {
      setIsLoadingChildData(true);
      try {
        const response = await getApi(
          `/shipment/by-service-center/${serviceCenterId}`,
          {
            salesOrderNumber,
            page,
            pageSize: 10,
            sortField,
            sortOrder:
              sortOrder === "ascend"
                ? "asc"
                : sortOrder === "descend"
                ? "desc"
                : null,
          }
        );

        if (!isMounted.current) return { data: [], total: 0 };

        const { data, total } = response.data.data;
        return { data, total };
      } catch (error) {
        console.error("Error fetching child shipments:", error);
        return { data: [], total: 0 };
      } finally {
        setIsLoadingChildData(false);
      }
    },
    []
  );

  // Function to open modal and fetch child data
  const handleActionClick = useCallback(
    async (record: any) => {
      setModalData(record);
      setIsModalVisible(true);
      setChildPage(1);
      setModalSelectedRowKeys([]);
      setBulkUpdateDate(null);
      setIsModalLoading(true);
      try {
        // Get the latest children data from allShipmentData
        const existingChildren = allShipmentData
          .filter(
            (item) =>
              item.serviceCenterId === record.serviceCenterId &&
              item.salesOrderNumber === record.salesOrderNumber
          )
          .flatMap((item) => item.children || []);

        setChildData(existingChildren);
        setChildTotal(existingChildren.length);
      } catch (error) {
        console.error("Error loading modal data:", error);
      } finally {
        // Minimum loading time for smooth modal opening
        setTimeout(() => {
          if (isMounted.current) {
            setIsModalLoading(false);
          }
        }, 200);
      }
    },
    [allShipmentData]
  );

  // Function to fetch more child data for infinite scroll
  const fetchMoreChildData = useCallback(async () => {
    if (!modalData) return;

    const response = await fetchShipmentsByGroup({
      serviceCenterId: modalData.serviceCenterId,
      salesOrderNumber: modalData.salesOrderNumber,
      page: childPage,
      sortField: childSortField,
      sortOrder: childSortOrder,
    });

    if (!isMounted.current) return;

    const combined = [...childData, ...response.data];
    setChildData(combined);
    setChildPage(childPage + 1);
    setChildTotal(response.total);
  }, [
    modalData,
    childPage,
    childSortField,
    childSortOrder,
    childData,
    fetchShipmentsByGroup,
  ]);

  // Handle child table sorting
  const handleChildTableSort = useCallback(
    async (pagination: any, filters: any, sorter: any) => {
      if (sorter && sorter.columnKey && modalData) {
        setChildSortField(sorter.columnKey);
        setChildSortOrder(sorter.order);
        setIsModalLoading(true);

        setChildData([]);
        setChildPage(1);

        const response = await fetchShipmentsByGroup({
          serviceCenterId: modalData.serviceCenterId,
          salesOrderNumber: modalData.salesOrderNumber,
          page: 1,
          sortField: sorter.columnKey,
          sortOrder: sorter.order,
        });

        if (isMounted.current) {
          setChildData(response.data);
          setChildPage(2);
          setChildTotal(response.total);
          setIsModalLoading(false);
        }
      }
    },
    [modalData, fetchShipmentsByGroup]
  );

  // Modal row selection
  const getModalRowSelection = useCallback(():
    | TableRowSelection<any>
    | undefined => {
    if (isServiceCenter) return undefined;

    return {
      selectedRowKeys: modalSelectedRowKeys.map((item) => item.id),
      onChange: (keys: any, selectedRows) => {
        // Filter out shipped items from selection
        const validSelectedRows = selectedRows.filter(
          (row) => row.status !== "SHIPPED"
        );

        const selectedWithQuantity = validSelectedRows.map((row) => ({
          id: row.id,
          quantity: row.quantity,
        }));

        setModalSelectedRowKeys(selectedWithQuantity);

        // Clear date if no items are selected
        if (selectedWithQuantity.length === 0) {
          setBulkUpdateDate(null);
        }
      },
      getCheckboxProps: (record) => ({
        disabled:
          record.status === "SHIPPED" || isLoadingChildData || isBulkUpdating,
        title:
          record.status === "SHIPPED"
            ? "Already shipped items cannot be selected"
            : "",
      }),
    };
  }, [
    isServiceCenter,
    modalSelectedRowKeys,
    isLoadingChildData,
    isBulkUpdating,
  ]);

  // IMPROVED: Handle bulk update in modal with smooth loading
  const handleModalBulkUpdate = useCallback(async () => {
    if (!modalSelectedRowKeys.length || !bulkUpdateDate) return;

    const data = {
      deliveryDate: dayjs(bulkUpdateDate).format("YYYY-MM-DD"),
      shipmentIds: modalSelectedRowKeys,
    };

    setIsBulkUpdating(true);

    try {
      const response = await putApi("/shipment/bulk-update", data);
      const message =
        response?.data?.message || "Shipments status updated successfully.";
      toastText(message, "success");

      // Update the status of selected items immediately
      const updatedChildData = childData.map((item) => {
        const isSelected = modalSelectedRowKeys.some(
          (selected) => selected.id === item.id
        );
        if (isSelected) {
          return {
            ...item,
            status: "SHIPPED",
            deliveryDate: bulkUpdateDate,
          };
        }
        return item;
      });

      setChildData(updatedChildData);
      setModalSelectedRowKeys([]);
      setBulkUpdateDate(null);

      // Refresh main table data
      await fetchShipments({
        skipDuplicateCheck: true,
        pageNumber: currentPage,
        pageSize: pageSize,
        searchParams: { fields: searchFields, value: searchValue.trim() },
      });
      setSelectedRowId(null);

      handleModalClose();
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        "Something went wrong while updating shipments status.";
      toastText(message, "error");
    } finally {
      setIsBulkUpdating(false);
    }
  }, [
    modalSelectedRowKeys,
    bulkUpdateDate,
    childData,
    fetchShipments,
    currentPage,
    pageSize,
    searchFields,
    searchValue,
  ]);

  // Enhanced edit handler
  const handleEditClick = useCallback(
    (data: any) => {
      setModalSelectedRowKeys([]);
      setBulkUpdateDate(null);
      setLocalUpdates({});
      setSelectedShipment(data);
      openModalHandler();
    },
    [setSelectedShipment, openModalHandler]
  );

  // Handle pagination change
  const paginationChangeHandler = useCallback(
    (page: number, size: number) => {
      setCurrentPage(page);
      setPageSize(size);

      if (fetchShipments) {
        fetchShipments({
          page: page,
          pageSize: size,
          skipDuplicateCheck: true,
        });
      }
    },
    [setCurrentPage, setPageSize, fetchShipments]
  );

  const handleBulkUpdateDate = useCallback((dateString: string | null) => {
    setBulkUpdateDate(dateString);
  }, []);

  const handleMarkAsShipped = useCallback(async () => {
    if (!selectedRowKeys.length || !bulkUpdateDate) return;

    const data = {
      deliveryDate: bulkUpdateDate,
      shipmentIds: selectedRowKeys,
    };

    setIsBulkUpdating(true);

    try {
      const response = await putApi("/shipment/bulk-update", data);
      const message =
        response?.data?.message || "Shipments status updated successfully.";
      toastText(message, "success");

      await fetchShipments({
        page: currentPage,
        pageSize: pageSize,
        skipDuplicateCheck: true,
      });
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        "Something went wrong while updating shipments status.";
      toastText(message, "error");
    } finally {
      setSelectedRowKeys([]);
      setBulkUpdateDate(null);
      setIsBulkUpdating(false);
    }
  }, [
    selectedRowKeys,
    bulkUpdateDate,
    fetchShipments,
    currentPage,
    pageSize,
    setSelectedRowKeys,
  ]);

  // Enhanced modal close handler
  const handleModalClose = useCallback(() => {
    setIsModalVisible(false);
    setModalData(null);
    setChildData([]);
    setModalSelectedRowKeys([]);
    setBulkUpdateDate(null);
    setChildPage(1);
    setChildSortField(null);
    setChildSortOrder(null);
    setLocalUpdates({});
    setIsModalLoading(false);
    setIsLoadingChildData(false);
  }, []);

  // Fixed bulk date change handler
  const handleBulkDateChange = useCallback(
    (date: any) => {
      if (modalSelectedRowKeys.length > 0) {
        const normalizedDate = date ? dayjs(date) : null;

        if (normalizedDate && normalizedDate.isValid()) {
          setBulkUpdateDate(normalizedDate.startOf("day").toISOString());

          const updatedLocalUpdates = { ...localUpdates };
          modalSelectedRowKeys.forEach((item) => {
            updatedLocalUpdates[item.id] = {
              ...updatedLocalUpdates[item.id],
              deliveryDate: normalizedDate.startOf("day").toISOString(),
            };
          });
          setLocalUpdates(updatedLocalUpdates);
        } else {
          setBulkUpdateDate(null);
          setLocalUpdates({});
        }
      } else {
        setBulkUpdateDate(null);
        setLocalUpdates({});
      }
    },
    [modalSelectedRowKeys, localUpdates]
  );

  // Monitor checkbox selection
  useEffect(() => {
    if (modalSelectedRowKeys.length === 0) {
      setBulkUpdateDate(null);
      setLocalUpdates({});
    }
  }, [modalSelectedRowKeys]);

  // Handle cleanup when modal closes
  useEffect(() => {
    if (!isModalVisible) {
      setModalSelectedRowKeys([]);
      setBulkUpdateDate(null);
      setLocalUpdates({});
    }
  }, [isModalVisible]);

  // Memoized table loading state
  const tableLoadingState = useMemo(() => {
    return isLoading || isTableLoading || isBulkUpdating;
  }, [isLoading, isTableLoading, isBulkUpdating]);

  // Memoized modal loading state
  const modalLoadingState = useMemo(() => {
    return isModalLoading || isBulkUpdating;
  }, [isModalLoading, isBulkUpdating]);

  const handleDateChange = async (date, dateString, record) => {
    // deliveryDate: values.deliveryDate
    //   ? dayjs(values.deliveryDate)
    //       .set('hour', dayjs().hour())
    //       .set('minute', dayjs().minute())
    //       .set('second', dayjs().second())
    //       .format("YYYY-MM-DDTHH:mm:ss")

    dateString = dateString
      ? dayjs(dateString)
          .set("hour", dayjs().hour())
          .set("minute", dayjs().minute())
          .set("second", dayjs().second())
          .format("YYYY-MM-DDTHH:mm:ss")
      : null;
    setSelectedRecordId(record.shipmentId);
    setSelectedDate(dateString);
  };
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);

  const apiCallToChangeDate = async (idsData: any) => {
    const response = await putApi(
      `/shipment/update-shipment-salesorderdate/${recordId}`,
      { salesOrderDate: selectedDate, shipmentIds: idsData }
    );
    fetchShipments({
      refetchAgain: true,
    });
    if (response.data.success) {
      toastText("Sales Order date updated successfully.", "success");
      // Update the local data immediately
      // setParentData((prevData) =>
      //   prevData.map((item) =>
      //     item.shipmentId === record.shipmentId
      //       ? { ...item, salesOrderDate: dateString }
      //       : item
      //   )
      // );
    } else {
      toastText("Failed to update Sales Order date.", "error");
    }
  };

  useEffect(() => {
    if (selectedDate && recordId) {
      const existingChildren: any = allShipmentData.filter(
        (x: any) => x.shipmentId === recordId
      );
      if (existingChildren.length === 0) {
        return;
      } else {
        const idsData: any = existingChildren[0].children.map((x: any) => x.id);
        apiCallToChangeDate(idsData);
      }
      // existingChildren[0].children = existingChildren[0].children.map(x=>x.
      // Call the API to update the date
    }
  }, [selectedDate]);

  return (
    <div className={styles["shipment-table__wrapper"]}>
      <div className={styles["dropdown__wrapper"]}>
        <DropdownComponent
          options={
            serviceCenters.length > 1
              ? [{ value: "all", label: "All" }, ...serviceCenters]
              : serviceCenters
          }
          placeholder="Select Service Center"
          value={
            serviceCenters.length === 0
              ? []
              : serviceCenters.length === 1
              ? [serviceCenters[0].value]
              : selectedServiceCenter && Array.isArray(selectedServiceCenter)
              ? selectedServiceCenter
              : []
          }
          onChange={handleServiceCenterChange}
          isLoading={isLoading}
        />
      </div>
      {/* Search and Filter */}
      <SearchAndFilter
        performSearchHandler={performSearchHandler}
        searchValue={searchValue}
        searchFields={searchFields}
        options={SEARCHABLE_FIELDS}
        onBulkUpdateDate={handleBulkUpdateDate}
        onMarkAsShipped={handleMarkAsShipped}
        disableMarkAsShipped={
          !selectedRowKeys.length || !bulkUpdateDate || isBulkUpdating
        }
        isServiceCenter={isServiceCenter}
        selectedRowKeys={selectedRowKeys}
        handleServiceCenterChange={handleServiceCenterChange}
        selectedServiceCenter={selectedServiceCenter}
        onApply={handleOk}
        handleSalesOrderNumberChange={handleSalesOrderNumberChange}
        selectedSalesOrderNumbers={selectedSalesOrderNumbers}
        setFiltersReset={setFiltersReset}
        fetchShipment={fetchShipments}
        salesOrderNumberOptions={salesOrderNumberOptions}
        fetchSalesOrderNumber={fetchSalesOrderNumber}
        selectedStatus={selectedStatus}
        handleStatusChange={handleStatusChange}
        setSelectedStatus={setSelectedStatus}
        handleFilterCall={handleFilterCall}
        clearFilter={clearFilter}
        handleDeleteClick={handleDeleteClick}
        canDelete={props.canDelete}
      />

      {/* Main Table - IMPROVED with smooth loading */}
      <Table
        dataSource={parentData}
        rowKey={getGroupKey}
        loading={{
          spinning: tableLoadingState,
          tip: isBulkUpdating ? "Updating shipments..." : "Loading...",
        }}
        scroll={{ y: "calc(100vh - 490px)", x: "63vh" }}
        className="table-global"
        rowSelection={rowSelection}
        onChange={tableChangeHandler}
        pagination={{
          total: totalRecords,
          current: currentPage,
          pageSize: pageSize,
          onChange: paginationChangeHandler,
          pageSizeOptions: pageSizeOptionsPaging,
          showSizeChanger: true,
        }}
      >
        <Column
          title="Service Center"
          dataIndex="serviceCenterName"
          key="serviceCenterName"
          className="bg-white"
          showSorterTooltip={{ title: "" }}
          defaultSortOrder={null}
          sorter
          sortDirections={["ascend", "descend", "ascend"]}
          sortIcon={(data) => <SortingIcon data={data} />}
        />
        <Column
          title="3nStar Sales Order"
          dataIndex="salesOrderNumber"
          key="salesOrderNumber"
          className="bg-white"
          showSorterTooltip={{ title: "" }}
          defaultSortOrder={null}
          sorter
          sortDirections={["ascend", "descend", "ascend"]}
          sortIcon={(data) => <SortingIcon data={data} />}
        />
        <Column
          title="Sales Order Date"
          dataIndex="salesOrderDate"
          key="salesOrderDate"
          className="bg-white"
          render={(text: string, record: any, index: number) => {
            const recordKey =
              record.shipmentId || record.id || record.key || index;
            const isEditing = selectedRowId === recordKey;

            const iseditable = record.statusC !== "SHIPPED";

            if (!iseditable) {
              return (
                <div className="opacity-50 pointer-events-none p-2 rounded">
                  {text ? moment(text).format("MM/DD/YYYY") : "-"}
                </div>
              );
            }

            if (isEditing) {
              setUpdateRecords(record);
              return (
                <DatePicker
                  // value={text ? moment(text) : null}
                  format="MM/DD/YYYY"
                  size="small"
                  onChange={(date, dateString) => {
                    handleDateChange(date, dateString, record);
                    setSelectedRowId(null); // Close picker after selection
                  }}
                  onOpenChange={(open) => {
                    if (!open) {
                      // Add a small delay to prevent immediate closing
                      setTimeout(() => {
                        setSelectedRowId(null);
                      }, 100);
                    }
                  }}
                  open={true}
                  allowClear
                  placeholder="Select date"
                  style={{ width: "50%" }}
                  getPopupContainer={() => document.body}
                  dropdownClassName="datepicker-no-scroll"
                />
              );
            }

            return (
              <div
                className="cursor-pointer hover:bg-gray-100 p-2 rounded"
                onClick={(e) => {
                  e.stopPropagation();

                  setSelectedRowId(recordKey);
                }}
              >
                {text ? moment(text).format("MM/DD/YYYY") : "-"}
              </div>
            );
          }}
          showSorterTooltip={{ title: "" }}
          sorter
          sortDirections={["ascend", "descend", "ascend"]}
          sortIcon={(data) => <SortingIcon data={data} />}
        />
        <Column
          title="Quantity"
          dataIndex="shipmentQty"
          key="shipmentQty"
          className="bg-white"
          render={(text: string) => <div>{text}</div>}
        />
        <Column
          title="Status"
          dataIndex="statusC"
          key="statusC"
          className="bg-white"
          render={(status: string) => {
            const cfg =
              SHIPMENT_STATUS_CONFIG[
                status as keyof typeof SHIPMENT_STATUS_CONFIG
              ] || SHIPMENT_STATUS_CONFIG.DEFAULT;

            const formatted = status
              .replace(/_/g, " ")
              .toLowerCase()
              .replace(/\b\w/g, (char) => char.toUpperCase());

            return (
              <Tag
                style={{
                  backgroundColor: cfg.bg,
                  color: cfg.text,
                  borderRadius: 4,
                  padding: "0 8px",
                  fontWeight: 600,
                }}
              >
                {formatted}
              </Tag>
            );
          }}
          showSorterTooltip={{ title: "" }}
          sorter
          sortDirections={["ascend", "descend", "ascend"]}
          sortIcon={(data) => <SortingIcon data={data} />}
        />
        <Column
          title="Actions"
          dataIndex="action"
          key="action"
          className="bg-white"
          render={(_, record: any) => (
            <div
              className="cursor-pointer"
              onClick={() => handleActionClick(record)}
              style={{
                cursor: tableLoadingState ? "not-allowed" : "pointer",
                opacity: tableLoadingState ? 0.6 : 1,
                pointerEvents: tableLoadingState ? "none" : "auto",
              }}
            >
              <EyeOutlined style={{ fontSize: "22px" }} className="eyeIcon" />
            </div>
          )}
        />
      </Table>

      {/* Modal for Child Data - IMPROVED with smooth loading */}
      <Modal
        className={styles["shipment-modal"]}
        title={`Service Center: ${modalData?.serviceCenterName || "Unknown"}`}
        open={isModalVisible}
        onCancel={handleModalClose}
        footer={[
          <Button
            key="close"
            size="large"
            onClick={handleModalClose}
            className="cancel-button"
            disabled={isBulkUpdating}
          >
            Cancel
          </Button>,
          !props.isServiceCenter && (
            <Button
              key="mark-shipped"
              type="primary"
              size="large"
              disabled={
                !modalSelectedRowKeys.length ||
                !bulkUpdateDate ||
                isBulkUpdating
              }
              onClick={handleModalBulkUpdate}
              loading={isBulkUpdating}
              style={{ fontSize: 15 }}
            >
              {isBulkUpdating ? "Updating..." : "Mark as Shipped"}
            </Button>
          ),
        ]}
        width={1000}
        centered
        destroyOnClose
      >
        {!props.isServiceCenter && modalSelectedRowKeys.length > 0 && (
          <div className={styles["shipment-modal__bulk-update"]}>
            <span>Bulk Update Delivery Date: </span>
            <DatePicker
              placeholder="Bulk Update Delivery Date"
              className={styles["shipment-modal__date-picker"]}
              value={bulkUpdateDate ? dayjs(bulkUpdateDate) : null}
              onChange={handleBulkDateChange}
              format="MM/DD/YYYY"
              // disabled={modalSelectedRowKeys.length === 0 || isBulkUpdating}
              // disabledDate={(current) =>
              //   current && current.isAfter(dayjs().endOf("day"))
              // }
              style={{ marginBottom: 25, width: "18vw" }}
              getPopupContainer={() => document.body}
              showTime={false}
              inputReadOnly
              defaultPickerValue={dayjs()}
            />
          </div>
        )}

        <div
          className={styles["shipment-modal__table-container"]}
          id="modal-scroll-container"
        >
          {isLoadingChildData && childData.length === 0 ? (
            <div className={styles["shipment-modal__spinner"]}>
              <Spin size="large" tip="Loading child data..." />
            </div>
          ) : (
            <InfiniteScroll
              dataLength={childData.length}
              next={fetchMoreChildData}
              hasMore={childTotal > childData.length}
              loader={
                <div className={styles["shipment-modal__spinner"]}>
                  <Spin size="small" />
                </div>
              }
              scrollableTarget="modal-scroll-container"
              endMessage={
                <p className={styles["shipment-modal__end-message"]}>
                  {childData.length === 0 ? "No records found" : null}
                </p>
              }
            >
              <Table
                dataSource={childData}
                rowKey="id"
                pagination={false}
                onChange={handleChildTableSort}
                rowSelection={
                  !props.isServiceCenter ? getModalRowSelection() : undefined
                }
                loading={{
                  spinning: modalLoadingState || isLoading,
                  tip: isBulkUpdating ? "Updating..." : "Loading...",
                }}
                size="large"
                className={styles["shipment-modal__table"]}
                scroll={{ y: 300 }}
                bordered={false}
              >
                <Column
                  title="Delivery Date"
                  dataIndex="deliveryDate"
                  key="deliveryDate"
                  width="18%"
                  render={(t: string, record: any) => {
                    const localUpdate = localUpdates[record.id];
                    const displayDate = localUpdate?.deliveryDate || t;
                    return displayDate
                      ? moment(displayDate).format("MM/DD/YYYY")
                      : "-";
                  }}
                />
                <Column
                  title="Spare Part Item"
                  dataIndex="itemNumber"
                  key="itemNumber"
                  width="18%"
                />
                <Column
                  title="Item Description"
                  dataIndex="itemDescription"
                  key="itemDescription"
                  width="20%"
                  render={(t: string) => {
                    if (invalidText(t)) return "-";
                    if (t.length > 25) {
                      return (
                        <Popover content={t} trigger="hover">
                          {t.slice(0, 25)}…
                        </Popover>
                      );
                    }
                    return t;
                  }}
                />
                <Column
                  title="Quantity"
                  dataIndex="quantity"
                  key="quantity"
                  width="12%"
                  render={(v) => <div style={{ textAlign: "center" }}>{v}</div>}
                />
                <Column
                  title="Status"
                  dataIndex="status"
                  key="status"
                  width="14%"
                  render={(s: string, record: any) => {
                    const localUpdate = localUpdates[record.id];
                    const displayStatus = localUpdate?.status || s;

                    const cfg =
                      SHIPMENT_STATUS_CONFIG[
                        displayStatus as keyof typeof SHIPMENT_STATUS_CONFIG
                      ] || SHIPMENT_STATUS_CONFIG.DEFAULT;
                    const label = displayStatus
                      .replace(/_/g, " ")
                      .toLowerCase()
                      .replace(/\b\w/g, (c) => c.toUpperCase());
                    return (
                      <Tag
                        style={{
                          backgroundColor: cfg.bg,
                          color: cfg.text,
                          border: "none",
                          borderRadius: "0.8rem",
                          padding: "0.2rem 0.8rem",
                          fontWeight: 600,
                        }}
                      >
                        {label}
                      </Tag>
                    );
                  }}
                />
                <Column
                  title="Actions"
                  dataIndex="action"
                  key="action"
                  width="10%"
                  render={(_, data: any) => (
                    <Space size={20} style={{ display: "flex" }}>
                      {["DRAFT", "READY_TO_SHIP"].includes(data.status) &&
                      !isServiceCenter ? (
                        <div
                          className="cursor-pointer flex align-center justify-center"
                          onClick={() => handleEditClick(data)}
                          style={{
                            cursor: modalLoadingState
                              ? "not-allowed"
                              : "pointer",
                            opacity: modalLoadingState ? 0.5 : 1,
                            pointerEvents: modalLoadingState ? "none" : "auto",
                          }}
                        >
                          <EditActionSvg />
                        </div>
                      ) : (
                        <div
                          className="flex align-center justify-center"
                          style={{
                            opacity: 0.3,
                            cursor: "not-allowed",
                          }}
                        >
                          <EditActionSvg />
                        </div>
                      )}
                    </Space>
                  )}
                />
              </Table>
            </InfiniteScroll>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default ShipmentTable;
